/**
 * API client for the Matcha admin panel.
 * Uses Shopify App Bridge session token auth when embedded,
 * or falls back to a configured bearer token.
 */

const API_BASE = import.meta.env.VITE_API_URL ?? 'https://app.ohmygoods.com';

async function getSessionToken(): Promise<string | null> {
  try {
    // @shopify/app-bridge v4 exposes shopify global when CDN script loaded with data-api-key
    const shopify = (window as any).shopify;

    if (shopify?.idToken) {
      // Add timeout to prevent hanging if idToken() never resolves
      const token = await Promise.race([
        shopify.idToken(),
        new Promise<null>((_, reject) =>
          setTimeout(() => reject(new Error('idToken timeout after 5s')), 5000)
        ),
      ]);
      return token;
    }
  } catch (err) {
    console.error('[Admin] Failed to get session token:', err);
  }
  return null;
}

export async function getAuthHeader(): Promise<string> {
  // Try Shopify session token first
  const sessionToken = await getSessionToken();
  if (sessionToken) {
    return `Bearer ${sessionToken}`;
  }

  // Fallback: use env-configured token (for dev/standalone)
  const devToken = import.meta.env.VITE_ADMIN_TOKEN;
  if (devToken) {
    return `Bearer ${devToken}`;
  }

  return '';
}

export async function apiFetch<T = any>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const authHeader = await getAuthHeader();

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: authHeader,
      ...options.headers,
    },
  });

  if (!res.ok) {
    const body = await res.text();
    throw new Error(`API Error ${res.status}: ${body}`);
  }

  return res.json();
}

export async function apiGet<T = any>(path: string): Promise<T> {
  return apiFetch<T>(path);
}

export async function apiPost<T = any>(path: string, body?: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
}

export async function apiPut<T = any>(path: string, body?: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  });
}

export async function apiDelete<T = any>(path: string): Promise<T> {
  return apiFetch<T>(path, { method: 'DELETE' });
}
